import React, { createContext, useContext, ReactNode, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { useDidomi, ConsentStatus } from '@/hooks/useDidomi';
import { useATTContext } from '@/components/ATTProvider';

let setDidomiConsentCheck: ((callback: () => Promise<boolean>) => void) | null = null;
let setFacebookDidomiConsentCheck: ((callback: () => Promise<boolean>) => void) | null = null;

if (Platform.OS !== 'web') {
  try {
    const nativeAnalytics = require('@/src/analytics/native');
    setDidomiConsentCheck = nativeAnalytics.setDidomiConsentCheck;
  } catch (error) {
    console.warn('[Didomi] Failed to load analytics consent integration:', error);
  }

  try {
    const facebookTracking = require('@/src/analytics/facebookTracking.native');
    setFacebookDidomiConsentCheck = facebookTracking.setDidomiConsentCheck;
  } catch (error) {
    console.warn('[Didomi] Failed to load Facebook consent integration:', error);
  }
}

interface DidomiContextValue extends ConsentStatus {
  showPreferences: () => void;
  getUserConsentStatus: (purposeId: string) => Promise<boolean> | boolean;
  setUserConsent: (purposeId: string, enabled: boolean) => void;
  updateConsentStatus: () => void;
}

const DidomiContext = createContext<DidomiContextValue | null>(null);

interface DidomiProviderProps {
  children: ReactNode;
}

export function DidomiProvider({ children }: DidomiProviderProps) {
  const att = useATTContext();

  // Memoize ATT status object to prevent infinite re-renders
  // Only create new object when ATT values actually change
  const attStatus = useMemo(() => {
    if (Platform.OS !== 'ios') return undefined;

    return {
      isLoading: att.isLoading,
      isTrackingAuthorized: att.isTrackingAuthorized,
      status: att.status,
    };
  }, [att.isLoading, att.isTrackingAuthorized, att.status]);

  // Pass ATT status to useDidomi hook (iOS only)
  const didomi = useDidomi(attStatus);

  useEffect(() => {
    if (!didomi.isReady) return;

    if (Platform.OS === 'ios' && att.isLoading) {
      console.log('[Didomi] ⏳ Waiting for ATT authorization before setting up consent integration...');
      return;
    }

    console.log('[Didomi] SDK ready, setting up analytics consent integration');

    if (setDidomiConsentCheck && Platform.OS !== 'web') {
      setDidomiConsentCheck(async () => {
        if (Platform.OS === 'ios' && !att.isTrackingAuthorized) {
          console.log('[Didomi] ATT not authorized, blocking analytics consent');
          return false;
        }
        return didomi.hasAnalyticsConsent;
      });
      console.log('[Didomi] Analytics consent check registered (gates all Snowplow tracking including video)');
      if (Platform.OS === 'ios') {
        console.log('[Didomi] iOS: ATT authorization also required for tracking');
      }
    }

    if (setFacebookDidomiConsentCheck && Platform.OS !== 'web') {
      setFacebookDidomiConsentCheck(async () => {
        if (Platform.OS === 'ios' && !att.isTrackingAuthorized) {
          console.log('[Didomi] ATT not authorized, blocking Facebook tracking');
          return false;
        }
        return didomi.hasAnalyticsConsent;
      });
      console.log('[Didomi] Facebook consent check registered (gates all Facebook tracking)');
      if (Platform.OS === 'ios') {
        console.log('[Didomi] iOS: ATT authorization also required for Facebook tracking');
      }
    }
  }, [didomi.isReady, didomi.hasAnalyticsConsent, att.isLoading, att.isTrackingAuthorized]);

  return (
    <DidomiContext.Provider value={didomi}>
      {children}
    </DidomiContext.Provider>
  );
}

export function useDidomiContext() {
  const context = useContext(DidomiContext);
  if (!context) {
    throw new Error('useDidomiContext must be used within DidomiProvider');
  }
  return context;
}
